import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../auth.service';
import { environment } from 'environments/environment';
import { SERVICES } from '@core/assets/constants';
import { EmbedLoginService, ThemeService } from '@core/services';
import { Subscription } from 'rxjs';

const { contentful } = environment;

/**
 * Login screen. Resolves the service (sid) from the query params or a credentials
 * token, handles activation of new accounts and, if the login requires two-factor
 * authentication, redirects to the OTP verification flow.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public isContentfulActive = contentful.active;
  @ViewChild('loginForm', { static: false }) public loginForm: NgForm;
  @ViewChild('activationForm', { static: false }) public activationForm: NgForm;
  stage = 'login';
  loading = false;
  passwordClear = false;
  passwordStrength = false;
  supportUrl: string;
  serviceName: string = 'campus';
  emailError: any;
  passwordError: any;
  credentials = {
    username: '',
    password: '',
    sid: '',
    captchaValue: '',
    token: '',
    clientId: ''
  };

  public selDate;
  public captchaSiteKey: string;
  private isTestCaptcha = false;
  private paramsSubscription: Subscription = new Subscription();
  private testLoginSubscription: Subscription = new Subscription();
  private loginSubscription: Subscription = new Subscription();
  private accountActiveSubscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private translate: TranslateService,
    private embedLoginService: EmbedLoginService,
    private themeService: ThemeService
  ) {}

  /**
   * Initialises the login form: resolves the service (sid) from the query params
   * or a credentials token, configures the captcha and starts the login test.
   */
  public ngOnInit(): void {
    // reset login status
    const d: Date = new Date();

    // See https://gamelearn.atlassian.net/l/c/qusqsAMr
    const shouldAllowTestCaptcha = !!environment['RECAPTCHA_TEST_SITEKEY'];
    this.isTestCaptcha =
      shouldAllowTestCaptcha &&
      (localStorage.getItem('disableCaptcha') !== null || environment.DOMAIN === 'localhost');
    this.captchaSiteKey = this.isTestCaptcha
      ? environment['RECAPTCHA_TEST_SITEKEY']
      : environment.RECAPTCHA_SITEKEY;

    this.selDate = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate()
    };
    this.authService.logout();

    this.paramsSubscription = this.route.queryParamMap.subscribe(async (queryParams: ParamMap) => {
      if (queryParams.get('credentials')) {
        this.credentials.token = queryParams.get('credentials');
        const payload = await this.authService.verifyToken(this.credentials.token);
        this.credentials.sid = payload.sid;
        this.credentials.username = payload.email;
        this.credentials.clientId = payload.clientId;
        this.credentials.token = null;
      } else {
        this.credentials.sid = queryParams.get('sid');
        this.credentials.username = queryParams.get('username');
      }

      this.serviceName =
        this.credentials.sid && SERVICES[this.credentials.sid]
          ? SERVICES[this.credentials.sid]
          : 'campus';
      this.credentials.sid = environment.sids[this.serviceName];

      if (queryParams.has('app')) {
        // workaround to launch any app with his name instead of sid
        this.serviceName = queryParams.get('app').toLowerCase();
        this.credentials.sid = environment.sids[this.serviceName];
      }

      this.themeService.setTheme(this.serviceName);
      this.testServiceLogin();
      this.embedLoginService.prepareEmbedMode(this.credentials.sid);
    });
    this.translate.get('auth.title').subscribe((trans) => this.titleService.setTitle(trans));
    this.translate.get('urls.forgotEmailSupport').subscribe((trans) => (this.supportUrl = trans));
  }

  /**
   * Cancels all active subscriptions of the component.
   */
  public ngOnDestroy(): void {
    this.paramsSubscription.unsubscribe();
    this.testLoginSubscription.unsubscribe();
    this.loginSubscription.unsubscribe();
    this.accountActiveSubscription.unsubscribe();
  }

  /**
   * Returns to the username entry screen, clearing errors and password.
   */
  public changeUser(): void {
    this.credentials.username = '';
    this.credentials.password = '';
    this.stage = 'login';
    this.emailError = null;
    this.passwordError = null;
  }

  /**
   * Checks whether the entered username corresponds to an existing account and,
   * optionally, continues with the login if a password has already been filled in.
   * @param loginOnSuccess If `true`, chains the login after validating the username
   */
  public testServiceLogin(loginOnSuccess = true): void {
    if (this.credentials.username) {
      this.emailError = null;
      this.passwordError = null;
      this.loading = true;
      this.testLoginSubscription = this.authService.testServiceLogin(this.credentials).subscribe({
        next: () => {
          if (loginOnSuccess && this.credentials.password) {
            this.login();
          }
        },
        error: (error) => {
          this.setTestLoginError(this.loginForm, error);
        }
      });
    }
  }

  /**
   * Generates the activation token for the current user and navigates to the
   * account activation form.
   */
  public activate(): void {
    this.emailError = null;
    this.loading = true;
    let activationData = this.credentials;
    if (this.isTestCaptcha) {
      activationData = Object.assign({}, activationData, { isTestCaptcha: true });
    }
    this.accountActiveSubscription = this.authService
      .createActivationToken(activationData)
      .subscribe({
        next: (response) => {
          const { sid, clientId } = this.credentials;
          this.router.navigate(['../account/activate'], {
            queryParams: { token: response.token, sid, clientId },
            relativeTo: this.route
          });
        },
        error: (error) => {
          this.setLoginError(this.activationForm, error);
          this.loading = false;
        }
      });
  }

  /**
   * Authenticates the user. If the response indicates that the account requires
   * two-factor authentication, navigates to the OTP verification route instead of
   * completing the login directly.
   */
  private login(): void {
    this.emailError = null;
    this.loading = true;
    this.loginSubscription = this.authService.createServiceLogin(this.credentials).subscribe({
      next: (response) => {
        this.embedLoginService.onLoginSuccess(response);
      },
      error: (error) => {
        this.setLoginError(this.loginForm, error);
        this.loading = false;
      }
    });
  }

  /**
   * Applies the login test error to the form. If the account doesn't exist yet,
   * switches to the activation step.
   * @param form Affected form, to reset it after applying the error
   * @param error Error returned by the API
   */
  private setTestLoginError(form: NgForm, error?: any): void {
    if (error && error.errorCode === 'PASSWORD_NOT_FOUND') {
      if (error.payload.clientTerms) {
        sessionStorage.setItem('activationData', JSON.stringify(error.payload));
      }
      this.stage = 'activate';
    }
    this.emailError = error
      ? Object.assign({}, error, {
          errorCode: `apiErrors.${error.errorCode || 'SYSTEM_ERROR'}`,
          payload: { userEmail: this.credentials.username }
        })
      : null;
    this.resetForm(form);
  }

  /**
   * Resets the form with the current credentials and stops the loading state.
   * @param form Form to reset
   */
  private resetForm(form: NgForm): void {
    if (form) {
      form.resetForm(this.credentials);
    }
    this.loading = false;
  }

  /**
   * Applies the login error to the form and clears the entered password.
   * @param form Affected form, to reset it after applying the error
   * @param error Error returned by the API
   */
  private setLoginError(form: NgForm, error?: any): void {
    this.passwordError = error
      ? Object.assign({}, error, { errorCode: `apiErrors.${error.errorCode || 'SYSTEM_ERROR'}` })
      : null;
    this.credentials.password = '';
    this.resetForm(form);
  }
}
